import { createApp, type Component } from "vue";
import { createPinia } from "pinia";
import VueRecaptcha from "vue3-recaptcha-v2";
import * as Sentry from "@sentry/vue";
import VueSplide from "@splidejs/vue-splide";

import App from "./App.vue";
import router from "./router";
import "@/assets/tailwind.css";
import "@/assets/animate.css";
import { createI18nForConfig } from "./plugins/i18n";
import {
  getAppConfig,
  onAppConfigChange,
  loadAppConfigFromFlagsmith,
} from "./config/app-config";
import { resolveHostnameTrait } from "./config/resolve-organization";
import { useConfigStore } from "./stores/configStore";
import { useClientStore } from "./stores/clientStore";
import { axiosInstance } from "./services/axios.config";
import { loadClarityScript } from "./lib/clarity";
import { getFlagsmith } from "./config/flagsmith.js";

function renderBootstrapError(): void {
  const root = document.getElementById("app");

  if (!root) {
    return;
  }

  root.innerHTML =
    "<p>No se pudo iniciar la aplicación. Intentá recargar la página.</p>";
}

async function createAndMountApp(): Promise<void> {
  const config = getAppConfig();
  const i18n = createI18nForConfig(config);
  const app = createApp(App).use(i18n);

  const components = import.meta.glob<{ default: Component }>(
    "./components/*.vue",
    { eager: true }
  );

  Object.entries(components).forEach(([path, definition]) => {
    const componentName =
      path
        ?.split("/")
        ?.pop()
        ?.replace(/\.\w+$/, "") || "";

    app.component(componentName, definition.default);
  });

  const sentryEnv = config.sentry.env;
  Sentry.init({
    app,
    environment: sentryEnv,
    dsn: config.sentry.dsn,
    release: `${config.sentry.project}@${config.sentry.release}`,
    integrations: [
      new Sentry.Replay({
        maskAllText: false,
      }),
    ],
    trackComponents: true,
    debug: sentryEnv !== "production",
    tracesSampleRate: sentryEnv === "production" ? 0.5 : 1,
    replaysOnErrorSampleRate: 1,
    logErrors: sentryEnv !== "production",
  });

  app.use(VueRecaptcha, {
    siteKey: config.recaptchaSiteKey,
    alterDomain: false,
  });

  const pinia = createPinia();
  app.use(pinia);
  useConfigStore().hydrate(config);
  useClientStore().setClientName();

  axiosInstance.setBaseURL(config.baseUrl);

  onAppConfigChange((nextConfig) => {
    useConfigStore().hydrate(nextConfig);
    useClientStore().setClientName();
    axiosInstance.setBaseURL(nextConfig.baseUrl);
    i18n.global.locale.value = (nextConfig.clientName ||
      "rapihogar") as typeof i18n.global.locale.value;
  });

  app.use(router);
  app.use(VueSplide);

  if (config.clarityId) {
    loadClarityScript(config.clarityId);
  }

  await router.isReady();
  app.mount("#app");
}

export async function bootstrap(): Promise<void> {
  const hostname = resolveHostnameTrait();

  try {
    await loadAppConfigFromFlagsmith(hostname);
  } catch (error) {
    renderBootstrapError();
    throw error;
  }

  await createAndMountApp();
}
